import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
    Box, Typography, IconButton, List, ListItem, ListItemIcon, ListItemText, CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { createPaymentRequest } from '../../api/api';
import { useToast } from '../../components/ToastProvider/ToastProvider';

const CURRENCIES = ['USD', 'MUR', 'EUR'];

const initialFormData = {
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    purpose: '',
    description: '',
    amount: '',
    currency: 'USD',
    expires_at: '',
};

export default function CreatePaymentRequestForm({ open, onClose, onSuccess }) {
    const { showToast } = useToast();
    const [formData, setFormData] = useState(initialFormData);
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleFileAdd = (e) => {
        const newFiles = Array.from(e.target.files);
        setFiles(prev => [...prev, ...newFiles]);
        e.target.value = '';
    };

    const handleFileRemove = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const validate = () => {
        const errs = {};
        if (!formData.customer_name.trim()) errs.customer_name = 'Required';
        if (!formData.customer_email.trim()) errs.customer_email = 'Required';
        else if (!/\S+@\S+\.\S+/.test(formData.customer_email)) errs.customer_email = 'Invalid email';
        if (!formData.purpose.trim()) errs.purpose = 'Required';
        if (!formData.amount || Number(formData.amount) <= 0) errs.amount = 'Must be > 0';
        if (!formData.expires_at) errs.expires_at = 'Required';
        else {
            const mutIso = `${formData.expires_at}:00+04:00`;
            const expDate = new Date(mutIso);
            if (expDate <= new Date()) errs.expires_at = 'Must be in the future';
            else if (expDate > new Date(Date.now() + 30 * 86400000)) errs.expires_at = 'Maximum 30 days from today';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('customer_name', formData.customer_name);
            fd.append('customer_email', formData.customer_email);
            fd.append('purpose', formData.purpose);
            fd.append('amount', formData.amount);
            fd.append('currency', formData.currency);
            // Treat the datetime-local value as Mauritius time (UTC+4) and convert to ISO/UTC
            const localDt = formData.expires_at; // e.g. "2026-03-15T18:00"
            const mutIso = `${localDt}:00+04:00`;
            fd.append('expires_at', new Date(mutIso).toISOString());
            if (formData.customer_phone) fd.append('customer_phone', formData.customer_phone);
            if (formData.description) fd.append('description', formData.description);
            files.forEach(f => fd.append('files', f));

            await createPaymentRequest(fd);
            showToast('Payment request created and email sent!', 'success');
            setFormData(initialFormData);
            setFiles([]);
            setErrors({});
            onSuccess();
        } catch (e) {
            showToast(e.message || 'Failed to create payment request', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData(initialFormData);
        setFiles([]);
        setErrors({});
        onClose();
    };

    return (
        <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') handleClose(); }} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Create Payment Request
                <IconButton size="small" onClick={handleClose} sx={{ ml: 2 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Typography variant="subtitle2" color="textSecondary">Customer Information</Typography>
                    <TextField
                        name="customer_name" label="Customer Name" required fullWidth size="small"
                        value={formData.customer_name} onChange={handleChange}
                        error={!!errors.customer_name} helperText={errors.customer_name}
                    />
                    <Box display="flex" gap={2}>
                        <TextField
                            name="customer_email" label="Customer Email" required fullWidth size="small"
                            value={formData.customer_email} onChange={handleChange}
                            error={!!errors.customer_email} helperText={errors.customer_email}
                        />
                        <TextField
                            name="customer_phone" label="Phone (optional)" fullWidth size="small"
                            value={formData.customer_phone} onChange={handleChange}
                        />
                    </Box>

                    <Typography variant="subtitle2" color="textSecondary" mt={1}>Payment Details</Typography>
                    <TextField
                        name="purpose" label="Purpose of Payment" required fullWidth size="small"
                        value={formData.purpose} onChange={handleChange}
                        error={!!errors.purpose} helperText={errors.purpose}
                    />
                    <TextField
                        name="description" label="Description / Details (optional)" fullWidth size="small"
                        multiline rows={3}
                        value={formData.description} onChange={handleChange}
                    />
                    <Box display="flex" gap={2}>
                        <TextField
                            name="amount" label="Amount" required size="small" type="number"
                            value={formData.amount} onChange={handleChange}
                            error={!!errors.amount} helperText={errors.amount}
                            inputProps={{ min: 0, step: '0.01' }}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            name="currency" label="Currency" select size="small"
                            value={formData.currency} onChange={handleChange}
                            sx={{ minWidth: 100 }}
                        >
                            {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                    </Box>
                    <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <TextField
                                name="expires_at" label="Expiry Date" required size="small" type="datetime-local"
                                value={formData.expires_at} onChange={handleChange}
                                error={!!errors.expires_at} helperText={errors.expires_at || 'Maximum 30 days from today'}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: new Date(Date.now() + 4 * 3600000).toISOString().slice(0, 16),
                                    max: new Date(Date.now() + 30 * 86400000 + 4 * 3600000).toISOString().slice(0, 16),
                                }}
                                fullWidth
                            />
                            <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>
                                (Timezone: UTC +4)
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="subtitle2" color="textSecondary" mt={1}>Attachments (optional)</Typography>
                    <Button variant="outlined" startIcon={<UploadFileIcon />} component="label">
                        Add Documents
                        <input type="file" hidden multiple onChange={handleFileAdd} />
                    </Button>
                    {files.length > 0 && (
                        <List dense>
                            {files.map((f, i) => (
                                <ListItem key={i} secondaryAction={
                                    <IconButton edge="end" size="small" onClick={() => handleFileRemove(i)}><DeleteIcon fontSize="small" /></IconButton>
                                }>
                                    <ListItemIcon><DescriptionIcon /></ListItemIcon>
                                    <ListItemText primary={f.name} secondary={`${(f.size / 1024).toFixed(1)} KB`} />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                <Button
                    variant="contained" onClick={handleSubmit} disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={16} /> : null}
                >
                    {submitting ? 'Creating...' : 'Create & Send Email'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
